import { useParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../services/axios-instance";
import { USER_REPORT } from "../../../services/endpoints";
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { UserInterface } from "../../../interface/userInterface";
import { SELECTED_USER_ID_URL } from "../../../services/endpoints";
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Typography,
  Tab, 
  Tabs
} from "@mui/material";
import { LogEntry } from "../../../interface/logEntry";
import TimeLogTable from "../../elements/TimeLogTable";
import ClientSummaryTable from "../../elements/ClientSummaryTable";

type ActiveTab = 'timelog' | 'clientsummary';

// Styled Tab: Implements the dark active tab design
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'uppercase',
  fontSize: 14,
  fontWeight: 600,
  padding: theme.spacing(1.25, 2.5), // 10px 20px
  color: theme.palette.text.secondary,
  borderBottom: '3px solid transparent',
  minHeight: 48,
  backgroundColor: theme.palette.custom.lightGray,

  '&.Mui-selected': {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.custom.darkTeal,
    borderBottom: `3px solid ${theme.palette.custom.darkTeal}`,
    borderRadius: '4px 4px 0 0',
  },
}));

/**
 * Top Bar (Header) - Dark teal background with white text
 */
const TopBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.875, 3.75), // 15px 30px
  backgroundColor: theme.palette.custom.darkTeal,
  color: theme.palette.common.white,
  minHeight: 40,
}));

/**
 * Controls Bar - Contains tabs and filters
 */
const ControlsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.common.white,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  marginTop: theme.spacing(3),
  height: 56,
}));

/**
 * Filters Box - Contains month/year dropdowns
 */
const FiltersBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  paddingBottom: 0,
}));

// Generate month names using Intl.DateTimeFormat (localized to English)
const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(2000, i, 1))
);

const TimelogReport = () => {
    // State management
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [activeTab, setActiveTab] = useState<ActiveTab>('timelog');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);

    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    const prevYear = currentYear - 1;
    const yearsArray = [prevYear, currentYear, nextYear];

    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);

    const [user, setUser] = useState<UserInterface>({
        id: 0,
        name: "",
        email: "",
        role_id: 0,
        available_days: 0,
        available_days_left: 0,
        available_days_current: 0,
        available_days_left_current: 0,
        available_days_prev: 0,
        available_days_left_all: 0,
        available_days_left_prev: 0,
        available_days_next: 0,
        available_days_left_next: 0,
        clients: [],
    });

    const { id } = useParams<{ id: string }>();
    const userId = id || '0';

    // Event handlers (memoized for performance)
    const handleChangePage = useCallback((_event: unknown, newPage: number) => {
      setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(+event.target.value);
      setPage(0);
    }, []);

    const handleTabChange = useCallback((tab: ActiveTab) => {
      setActiveTab(tab);
    }, []);

    // Data fetching
    useEffect(() => {
      const controller = new AbortController();

      (async () => {
        console.log('Fetching data...');
        try {
          const logsResponse = await axiosInstance.get(USER_REPORT(+userId, selectedMonth, selectedYear), {
            signal: controller.signal
          });
          const logsData: LogEntry[] = logsResponse.data.data;
          setLogs(logsData);

          const userResponse = await axiosInstance.get(SELECTED_USER_ID_URL(+userId), {
            signal: controller.signal
          });
          const userData: UserInterface = userResponse.data.data;
          setUser(userData);
        } catch (err: unknown) {
          if (err instanceof Error && err.name === 'AbortError') {
            console.info('Request successfully aborted.');
          } else {
            console.error('Unexpected fetch error:', err);
          }
        }
      })();

      return () => controller.abort();
    }, [userId, selectedMonth, selectedYear]);


    return (
      // Base background set to white
      <Box sx={{ minHeight: '100vh', backgroundColor: 'white' }}> 
        
        {/* --- Top Bar (Header) --- */}
        <TopBar>
          <Box>
            <Typography
              component="span"
              variant="reportTitle"
              sx={{ fontWeight: 'bold', mr: 2 }}
            >
              {user.name}'s Time Report
            </Typography>
            <Typography
              component="span"
              variant="reportSubtitle"
              sx={{ color: 'custom.mediumGray' }}
            >
              ({user.email})
            </Typography>
          </Box>
          <Typography
            variant="reportSubtitle"
            sx={{ color: 'custom.mediumGray' }}
          >
            {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
          </Typography>
        </TopBar>

        <ControlsBar>
          
          <Tabs 
            value={activeTab} 
            onChange={(_e, newValue) => handleTabChange(newValue as ActiveTab)} 
            TabIndicatorProps={{ style: { display: 'none' } }} // Hide default indicator
            // ALIGNMENT FIX: Center the tab content
            sx={{ height: '100%' }}
          >
            <StyledTab label="TIME LOG" value="timelog" />
            <StyledTab label="CLIENT SUMMARY" value="clientsummary" />
          </Tabs>

          <FiltersBox>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="select-month">Month</InputLabel>
              <Select
                name="month"
                value={selectedMonth - 1}
                label="Month"
                labelId="select-month"
                id="select-month"
                onChange={(e) => { setSelectedMonth(+e.target.value + 1); }}
              >
                {MONTH_NAMES.map((month, i) => (
                  <MenuItem key={i} value={i}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Year Filter */}
            <FormControl variant="outlined" size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="select-year">Year</InputLabel>
              <Select
                name="year"
                value={selectedYear}
                label="Year"
                labelId="select-year"
                id="select-year"
                onChange={(e) => { setSelectedYear(+e.target.value); }}
              >
                {yearsArray.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FiltersBox>
        </ControlsBar>
        
        {/* --- Content View Area --- */}
        <Box sx={{ p: 3 }}>
        {logs.length > 0 ? (
            <>
              {activeTab === 'timelog' && (
                <TimeLogTable
                logs={logs}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
              {activeTab === 'clientsummary' && (
              <ClientSummaryTable logs={logs} />
              )}
            </>
          ) : (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No logs found for this month and year. **Try changing the filters above.**
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
    );
};

export default TimelogReport;